import styles from "./ActivityTitle.module.scss";
import RecurrenceIcon from "@medoma/components/icons/RecurrenceIcon";
import {
  categorySchema,
  type ISharedActivityOccurrenceFields,
} from "@medoma/models/activities";
import { Link } from "@medoma/components/Link/Link";
import type { IText } from "@medoma/components/Text/Text";
import { Text, TextWithLineBreaks } from "@medoma/components/Text/Text";
import { StatusTag } from "../StatusTag/StatusTag";
import clsx from "clsx";
import HiddenIcon from "@medoma/components/icons/HiddenIcon";

// Accepts every kind of activity occurrence, so it takes the fields they all
// share rather than the union — callers pass occurrences assembled from several
// sources, not always one whole variant.
type IActivityTitle = {
  activityOccurrence: ISharedActivityOccurrenceFields;
  linkTo?: string;
  showStatus?: boolean;
} & Pick<IText, "weight" | "size">;

export const ActivityTitle = ({
  activityOccurrence,
  linkTo,
  weight,
  size = "base",
  showStatus,
}: IActivityTitle) => {
  const {
    category,
    description,
    hidden: isHidden,
    recurring: isRecurring,
    title,
    status,
    note,
  } = activityOccurrence;

  const titleElement = linkTo ? (
    <Link to={linkTo} weight={weight} size={size} color="neutral">
      {title}
    </Link>
  ) : (
    <Text element="span" weight={weight} size={size} color="default">
      {title}
    </Text>
  );

  const noteAndDescription =
    !note && !description
      ? undefined
      : [note, description].filter(Boolean).join("\n");

  return (
    <div className={clsx(styles.wrapper, styles[`size-${size}`])}>
      {showStatus ? (
        <div className={styles.statusTagWrapper}>
          <StatusTag status={status} size="tiny" />
        </div>
      ) : null}
      <div className={styles.textContent}>
        <div className={styles.titleAndIcons}>
          {titleElement}
          {isRecurring && <RecurrenceIcon />}
          {isHidden && category !== categorySchema.enum.AdminTask && (
            <HiddenIcon />
          )}
        </div>
        <TextWithLineBreaks
          size={size === "large" ? "base" : size === "base" ? "small" : size}
          color="faded"
        >
          {noteAndDescription}
        </TextWithLineBreaks>
      </div>
    </div>
  );
};
